import type * as React from 'react'
import { useRef, useCallback } from 'react'
import { clsx } from 'clsx'

import { Popover } from '../../Popover'
import { ScrollArea } from '../../ScrollArea'
import { Text } from '../../Text'
import { Tooltip } from '../../Tooltip'
import { WarningIcon } from '../../icons'

import { useSuggestionSync } from '../hooks/useSuggestionSync'
import type { TimeSuggestion } from '../types'
import styles from '../SmartTimeInput.module.css'

type SuggestionMenuProps = {
  suggestions: TimeSuggestion[]
  activeIndex: number
  matchIndex: number
  userNavigated: boolean
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSuggestionSelect: (suggestion: TimeSuggestion) => void
  onActiveIndexChange: (
    index: number,
    userInitiated?: boolean,
    shouldScroll?: boolean,
  ) => void
  inputRef: React.RefObject<HTMLInputElement>
  listboxId: string
  children: React.ReactNode
  shouldScrollToActive: boolean
  inputValue: string
}

const ConditionalTooltip = ({
  children,
  tooltipContent,
}: {
  children: React.ReactNode
  tooltipContent?: string
}) => {
  if (!tooltipContent) {
    return <>{children}</>
  }
  return (
    <Tooltip align="start" delay={0} content={tooltipContent}>
      {children}
    </Tooltip>
  )
}

export const SuggestionMenu = ({
  suggestions,
  activeIndex,
  matchIndex,
  userNavigated,
  isOpen,
  onOpenChange,
  onSuggestionSelect,
  onActiveIndexChange,
  inputRef,
  listboxId,
  children,
  shouldScrollToActive,
  inputValue,
}: SuggestionMenuProps) => {
  const listRef = useRef<HTMLDivElement>(null)
  const menuContentRef = useRef<HTMLDivElement>(null)

  // Memoize callbacks to prevent infinite loops
  const handleAutoHighlight = useCallback(
    (index: number) => {
      onActiveIndexChange(index, false)
    },
    [onActiveIndexChange],
  )

  // Manage suggestion highlighting and synchronization
  useSuggestionSync({
    isOpen,
    activeIndex,
    matchIndex,
    userNavigated,
    suggestions,
    listRef,
    onAutoHighlight: handleAutoHighlight,
    onSetActiveIndex: onActiveIndexChange,
    shouldScrollToActive,
    inputValue,
  })

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange} modal={false}>
      <Popover.Anchor>{children}</Popover.Anchor>

      <Popover.Content
        className={styles.menu}
        align="start"
        side="bottom"
        sideOffset={4}
        onOpenAutoFocus={(ev: Event) => {
          ev.preventDefault()
        }}
        // Radix's ScrollArea scrollbar/thumb aren't focusable, so a mousedown there
        // would otherwise shift focus to this content's own focus-trap container
        // (it carries tabIndex=-1), blurring the input and closing the menu.
        onMouseDown={(event) => event.preventDefault()}
        onPointerDownOutside={(ev) => {
          // keep focus on input if the user clicks the input or any element within the input wrapper (e.g., prefix icon)
          const target = ev.target
          if (target instanceof Node && inputRef.current) {
            const inputWrapper = inputRef.current.parentElement
            if (inputWrapper?.contains(target)) {
              ev.preventDefault()
              // Ensure input maintains focus
              inputRef.current.focus({ preventScroll: true })
            }
          }
        }}
        onEscapeKeyDown={(ev) => {
          ev.preventDefault()
          inputRef.current?.focus({ preventScroll: true })
        }}
        ref={menuContentRef}
      >
        <ScrollArea className={styles.scrollArea}>
          <div
            ref={listRef}
            role="listbox"
            id={listboxId}
            aria-label="Time suggestions"
            className={styles.suggestionsList}
          >
            {suggestions.map((suggestion, index) => {
              const isActive = index === activeIndex
              const isAutoMatch = index === matchIndex && !userNavigated
              return (
                <ConditionalTooltip
                  key={`${suggestion.time.toString()}${
                    suggestion.disabled ? '-d' : ''
                  }`}
                  tooltipContent={suggestion.tooltipText}
                >
                  <div
                    id={`${listboxId}-option-${index}`}
                    role="option"
                    aria-selected={isActive}
                    aria-disabled={suggestion.disabled || undefined}
                    className={clsx(
                      styles.suggestion,
                      isAutoMatch && styles.suggestionAutoMatch,
                      isActive && styles.suggestionActive,
                      suggestion.disabled && styles.suggestionDisabled,
                    )}
                    tabIndex={-1}
                    onMouseEnter={() => onActiveIndexChange(index, true, false)}
                    onMouseDown={(event) => event.preventDefault()}
                    onTouchStart={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.preventDefault()
                      if (!suggestion.disabled) {
                        onSuggestionSelect(suggestion)
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        if (!suggestion.disabled) {
                          onSuggestionSelect(suggestion)
                        }
                      }
                    }}
                    data-autohighlight={isAutoMatch || undefined}
                  >
                    <Text>{suggestion.displayText}</Text>
                    {suggestion.variant === 'warning' && (
                      <WarningIcon
                        size="small"
                        className={styles.warningIcon}
                        aria-label="Warning"
                      />
                    )}
                  </div>
                </ConditionalTooltip>
              )
            })}
          </div>
        </ScrollArea>
      </Popover.Content>
    </Popover>
  )
}

SuggestionMenu.displayName = 'SuggestionMenu'

export type { SuggestionMenuProps }
